import React from 'react';
import HelpBadge from './components/HelpBadge';
import RazzmatazzInstallGuide from './RazzmatazzInstallGuide';
import {
  getSiteVisitorId,
  isSiteTrafficExcludedLocally,
  setSiteTrafficExcludedLocally,
} from './siteTraffic';

export type RazzmatazzAnalyticsOverview = {
  window_hours?: number;
  pageviews?: number;
  sessions?: number;
  visitors?: number;
  active_now?: number;
  bounce_rate_pct?: number;
  avg_session_duration_s?: number;
  pages_per_session?: number;
  top_paths?: Array<{ path: string; hits: number }>;
  entry_paths?: Array<{ path: string; hits: number }>;
  top_referrers?: Array<{ referrer: string; sessions: number }>;
  top_channels?: Array<{ channel: string; sessions: number }>;
  utm_sources?: Array<{ source: string; hits: number }>;
  utm_campaigns?: Array<{ campaign: string; hits: number }>;
  top_companies?: Array<{ org_name: string; sessions: number; asn?: string }>;
  identified_visitors?: number;
  devices?: Array<{ device: string; hits: number }>;
  browsers?: Array<{ browser: string; hits: number }>;
  languages?: Array<{ language: string; hits: number }>;
  countries?: Array<{ country: string; hits: number }>;
  regions?: Array<{ region: string; hits: number }>;
  cities?: Array<{ city: string; hits: number }>;
  hosts?: Array<{ host: string; hits: number }>;
  tracking_host?: string | null;
  geo_note?: string;
  duration_note?: string;
  by_day?: Array<{ day: string; pageviews: number; sessions: number; visitors: number }>;
  attention?: {
    summary?: Record<string, unknown>;
    where_eyeballs?: Array<Record<string, unknown>>;
    friction?: Array<Record<string, unknown>>;
    note?: string;
  };
  note?: string;
};

type SessionCompany = {
  org_name?: string;
  asn?: string;
  org_domain?: string;
  source?: string;
};

type SessionIdentity = {
  identified?: boolean;
  email?: string;
  full_name?: string;
  company_name?: string;
  source?: string;
};

type SessionAuthenticity = {
  score?: number;
  label?: string;
  reasons?: string[];
};

type SessionInteraction = {
  event_type: string;
  path: string;
  target?: string;
  label?: string;
  duration_ms?: number;
  at?: string;
};

type SessionRow = {
  session_id: string;
  visitor_id?: string;
  pageviews: number;
  started_at: string;
  last_at: string;
  duration_s: number;
  entry_path: string;
  exit_path: string;
  device: string;
  browser: string;
  os?: string;
  country: string;
  region?: string;
  city?: string;
  language: string;
  referrer: string;
  landing_referrer?: string;
  referrer_url?: string;
  channel: string;
  client_ip?: string;
  is_bot?: boolean;
  bot_name?: string;
  authenticity?: SessionAuthenticity;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  screen_width?: number | null;
  user_agent?: string;
  company?: SessionCompany;
  identity?: SessionIdentity;
  interactions?: SessionInteraction[];
  paths: Array<{
    path: string;
    at: string;
    title?: string;
    dwell_s?: number;
    dwell_ms?: number;
    gap_s?: number;
    signal?: string;
  }>;
};

type LinkedSite = {
  id: number;
  host: string;
  site_key: string;
  label?: string;
  verified?: boolean;
  snippet?: string;
  verification_token?: string;
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  language?: 'en' | 'es';
  /** Marketing host filter (e.g. jfgoss.com). Empty = all hosts. */
  host?: string;
};

type MetricKey = 'visitors' | 'sessions' | 'pageviews' | 'active';

type ExplainPlanStep = {
  when: string;
  title: string;
  detail: string;
  action?: string;
};

const isNonHumanSession = (row: SessionRow): boolean => {
  if (row.is_bot) return true;
  if (String(row.device || '').toLowerCase() === 'bot') return true;
  if (String(row.bot_name || '').trim()) return true;
  const authenticityLabel = String(row.authenticity?.label || '').toLowerCase();
  if (authenticityLabel === 'likely_bot' || authenticityLabel === 'bot') return true;
  const ua = String(row.user_agent || '').trim().toLowerCase();
  if (!ua) return true;
  if (/(?:^|[^a-z])(?:bot|crawl|spider|headless|lighthouse|puppeteer|playwright|selenium)(?:[^a-z]|$)/i.test(ua)) {
    return true;
  }
  const width = row.screen_width;
  if (
    typeof width === 'number'
    && (width === 0 || width === 800 || width === 1024)
    && ua.includes('linux')
    && ua.includes('chrome/')
    && !ua.includes('mobile')
    && !ua.includes('android')
  ) {
    return true;
  }
  return false;
};

const sessionIsPaid = (row: SessionRow): boolean => {
  const channel = String(row.channel || '').toLowerCase();
  const hasUtm = Boolean(row.utm_source || row.utm_medium || row.utm_campaign);
  return hasUtm || channel.includes('paid') || channel.includes('cpc') || channel.includes('ads');
};

const sessionUniqueKey = (row: SessionRow): string => {
  const visitor = String(row.visitor_id || '').trim();
  if (visitor) return `v:${visitor}`;
  const ip = String(row.client_ip || '').trim();
  if (ip && ip !== '—') return `ip:${ip}`;
  return `s:${String(row.session_id || '')}`;
};

const collapseUniqueSessions = (rows: SessionRow[]): SessionRow[] => {
  const best = new Map<string, SessionRow>();
  for (const row of rows) {
    const key = sessionUniqueKey(row);
    const prev = best.get(key);
    if (!prev) {
      best.set(key, row);
      continue;
    }
    const prevAt = String(prev.last_at || prev.started_at || '');
    const nextAt = String(row.last_at || row.started_at || '');
    if (nextAt >= prevAt) best.set(key, row);
  }
  return Array.from(best.values()).sort((a, b) =>
    String(b.last_at || b.started_at || '').localeCompare(String(a.last_at || a.started_at || '')),
  );
};

type DayMetricRow = { day: string; pageviews: number; sessions: number; visitors: number };

/** Build daily bars from the same filtered session set Details uses. */
const aggregateDaysFromSessions = (
  scaffold: DayMetricRow[],
  rows: SessionRow[],
  uniqueOnly: boolean,
): DayMetricRow[] => {
  const byDay = new Map<string, SessionRow[]>();
  for (const row of rows) {
    const day = String(row.started_at || row.last_at || '').slice(0, 10);
    if (!day) continue;
    const list = byDay.get(day) || [];
    list.push(row);
    byDay.set(day, list);
  }
  return scaffold.map((slot) => {
    const dayRows = byDay.get(slot.day) || [];
    const counted = uniqueOnly ? collapseUniqueSessions(dayRows) : dayRows;
    const visitors = new Set(dayRows.map(sessionUniqueKey)).size;
    const pageviews = dayRows.reduce((sum, row) => sum + Number(row.pageviews || 0), 0);
    return {
      day: slot.day,
      pageviews,
      sessions: counted.length,
      visitors,
    };
  });
};

const rankFromSessions = (
  rows: SessionRow[],
  pick: (row: SessionRow) => string,
  limit = 8,
): Array<{ label: string; value: number }> => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const label = String(pick(row) || '').trim() || '—';
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit);
};

const pathHitsFromSessions = (
  rows: SessionRow[],
  limit = 8,
): Array<{ label: string; value: number }> => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const trail = Array.isArray(row.paths) ? row.paths : [];
    if (trail.length > 0) {
      for (const step of trail) {
        const path = String(step?.path || '').trim() || '/';
        counts.set(path, (counts.get(path) || 0) + 1);
      }
      continue;
    }
    const fallback = String(row.entry_path || '/').trim() || '/';
    counts.set(fallback, (counts.get(fallback) || 0) + Number(row.pageviews || 1));
  }
  return Array.from(counts.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit);
};

type RangeQuery = { hours: number; since?: string; until?: string };

type RangePreset = {
  id: string;
  en: string;
  es: string;
  resolve: () => RangeQuery;
};

/** Plausible / Matomo / Assembled style: calendar periods first, then rolling windows. */
const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const startOfLocalWeek = (date: Date): Date => {
  const day = startOfLocalDay(date);
  // Monday start (ISO), matching Matomo “This week”.
  const weekday = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - weekday);
  return day;
};

const toIso = (date: Date): string => date.toISOString();

const hoursBetween = (start: Date, end: Date): number =>
  Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 3_600_000));

const RANGE_PRESETS: RangePreset[] = [
  {
    id: 'today',
    en: 'Today',
    es: 'Hoy',
    resolve: () => {
      const end = new Date();
      const start = startOfLocalDay(end);
      return { hours: hoursBetween(start, end), since: toIso(start), until: toIso(end) };
    },
  },
  {
    id: 'yesterday',
    en: 'Yesterday',
    es: 'Ayer',
    resolve: () => {
      const today = startOfLocalDay(new Date());
      const start = new Date(today);
      start.setDate(start.getDate() - 1);
      return { hours: 24, since: toIso(start), until: toIso(today) };
    },
  },
  {
    id: 'this_week',
    en: 'This week',
    es: 'Esta semana',
    resolve: () => {
      const end = new Date();
      const start = startOfLocalWeek(end);
      return { hours: hoursBetween(start, end), since: toIso(start), until: toIso(end) };
    },
  },
  {
    id: 'last_week',
    en: 'Last week',
    es: 'Semana pasada',
    resolve: () => {
      const thisWeek = startOfLocalWeek(new Date());
      const start = new Date(thisWeek);
      start.setDate(start.getDate() - 7);
      return { hours: 168, since: toIso(start), until: toIso(thisWeek) };
    },
  },
  {
    id: '7d',
    en: 'Last 7 days',
    es: 'Últimos 7 días',
    resolve: () => ({ hours: 168 }),
  },
  {
    id: '14d',
    en: 'Last 14 days',
    es: 'Últimos 14 días',
    resolve: () => ({ hours: 336 }),
  },
  {
    id: 'this_month',
    en: 'This month',
    es: 'Este mes',
    resolve: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
      return { hours: hoursBetween(start, end), since: toIso(start), until: toIso(end) };
    },
  },
  {
    id: 'last_month',
    en: 'Last month',
    es: 'Mes pasado',
    resolve: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth() - 1, 1, 0, 0, 0, 0);
      const until = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
      return { hours: hoursBetween(start, until), since: toIso(start), until: toIso(until) };
    },
  },
  {
    id: '28d',
    en: 'Last 28 days',
    es: 'Últimos 28 días',
    resolve: () => ({ hours: 672 }),
  },
  {
    id: '30d',
    en: 'Last 30 days',
    es: 'Últimos 30 días',
    resolve: () => ({ hours: 720 }),
  },
  {
    id: '90d',
    en: 'Last 90 days',
    es: 'Últimos 90 días',
    resolve: () => ({ hours: 2160 }),
  },
  {
    id: '12m',
    en: 'Last 12 months',
    es: 'Últimos 12 meses',
    resolve: () => ({ hours: 8760 }),
  },
  {
    id: 'ytd',
    en: 'Year to date',
    es: 'Año hasta hoy',
    resolve: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { hours: hoursBetween(start, end), since: toIso(start), until: toIso(end) };
    },
  },
];

const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds || 0));
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}m ${secs}s`;
};

/** Relative age from last activity (not session duration). */
const formatTimeAgo = (iso: string, language: 'en' | 'es', nowMs = Date.now()): string => {
  if (!iso) return language === 'es' ? '—' : '—';
  const then = new Date(String(iso).replace('Z', '+00:00')).getTime();
  if (!Number.isFinite(then)) return language === 'es' ? '—' : '—';
  const seconds = Math.max(0, Math.round((nowMs - then) / 1000));
  if (seconds < 15) return language === 'es' ? 'ahora' : 'just now';
  if (seconds < 60) return language === 'es' ? `hace ${seconds}s` : `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return language === 'es' ? `hace ${mins}m` : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return language === 'es' ? `hace ${hours}h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return language === 'es' ? `hace ${days}d` : `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return language === 'es' ? `hace ${weeks} sem` : `${weeks}w ago`;
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);
};

const parseDay = (isoDay: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDay || '').trim());
  if (!match) return new Date(NaN);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0);
};

const formatChartDay = (isoDay: string, locale: string, mode: 'tick' | 'full' | 'range'): string => {
  const date = parseDay(isoDay);
  if (Number.isNaN(date.getTime())) return isoDay;
  if (mode === 'full') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
  if (mode === 'range') {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const niceCeiling = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const scaled = value / base;
  const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return nice * base;
};

const RankList: React.FC<{
  title: React.ReactNode;
  empty: string;
  rows: Array<{ label: string; value: number }>;
}> = ({ title, empty, rows }) => {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <article className="razz-rank">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="razz-muted">{empty}</p>
      ) : (
        <ul className="razz-rank-list">
          {rows.map((row) => {
            const pct = Math.max(4, Math.round((row.value / max) * 100));
            return (
              <li key={row.label}>
                <div className="razz-rank-bar" style={{ width: `${pct}%` }} aria-hidden />
                <span className="razz-rank-label" title={row.label}>{row.label}</span>
                <span className="razz-rank-value">{formatNumber(row.value)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
};

const METRIC_LABELS: Record<MetricKey, { en: string; es: string }> = {
  visitors: { en: 'Visitors', es: 'Visitantes' },
  sessions: { en: 'Sessions', es: 'Sesiones' },
  pageviews: { en: 'Pageviews', es: 'Vistas' },
  active: { en: 'Active now', es: 'Activos ahora' },
};

const isActiveSession = (lastAt: string, withinMinutes = 5): boolean => {
  if (!lastAt) return false;
  try {
    const end = new Date(lastAt.replace('Z', '+00:00')).getTime();
    return Number.isFinite(end) && Date.now() - end <= withinMinutes * 60_000;
  } catch {
    return false;
  }
};

const pctChange = (current: number, previous: number): number | null => {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) {
    return current > 0 && previous === 0 ? 100 : null;
  }
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const DayChart: React.FC<{
  days: Array<{ day: string; pageviews: number; sessions: number; visitors: number }>;
  metric: Exclude<MetricKey, 'active'>;
  spanish: boolean;
  selectedDay?: string | null;
  onDaySelect?: (day: string) => void;
}> = ({ days, metric, spanish, selectedDay = null, onDaySelect }) => {
  const locale = spanish ? 'es' : 'en-US';
  const chronological = React.useMemo(() => [...days].reverse(), [days]);
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);

  const values = chronological.map((row) => Number(row[metric] || 0));
  const maxValue = Math.max(0, ...values);
  const yMax = niceCeiling(maxValue || 1);
  const yTicks = [0, yMax / 2, yMax];
  const metricLabel = spanish ? METRIC_LABELS[metric].es : METRIC_LABELS[metric].en;
  const firstDay = chronological[0]?.day;
  const lastDay = chronological[chronological.length - 1]?.day;
  const rangeLabel = firstDay && lastDay
    ? `${formatChartDay(firstDay, locale, 'range')} – ${formatChartDay(lastDay, locale, 'range')}`
    : '';

  const width = 640;
  const height = 220;
  const pad = { top: 18, right: 16, bottom: 36, left: 44 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const count = Math.max(1, chronological.length);
  const step = innerW / count;
  const barW = Math.min(28, Math.max(10, step * 0.55));

  const points = chronological.map((row, index) => {
    const value = Number(row[metric] || 0);
    const x = pad.left + step * index + step / 2;
    const y = pad.top + innerH - (value / yMax) * innerH;
    return { row, value, x, y, index };
  });

  const areaPath = points.length
    ? [
      `M ${points[0].x} ${pad.top + innerH}`,
      ...points.map((point) => `L ${point.x} ${point.y}`),
      `L ${points[points.length - 1].x} ${pad.top + innerH}`,
      'Z',
    ].join(' ')
    : '';
  const linePath = points.length
    ? points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
    : '';

  const active = hoverIndex == null ? null : points[hoverIndex];
  const tickEvery = count <= 10 ? 1 : count <= 20 ? 2 : Math.ceil(count / 8);

  return (
    <section className="razz-chart" aria-label={spanish ? 'Tendencia por día' : 'Daily trend'}>
      <div className="razz-chart-head">
        <div>
          <h3>{metricLabel}</h3>
          <p className="razz-muted">
            {rangeLabel
              || (spanish ? 'Sin días en este periodo.' : 'No days in this range yet.')}
          </p>
        </div>
        <p className="razz-muted razz-chart-hint">
          {spanish
            ? 'Un punto por día · clic abre Detalle de ese día'
            : 'One day per bar · click opens Details for that day'}
        </p>
      </div>

      {chronological.length === 0 ? (
        <p className="razz-muted">{spanish ? 'Aún no hay datos diarios.' : 'No daily data yet.'}</p>
      ) : (
        <div className="razz-chart-canvas">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${metricLabel}: ${rangeLabel}`}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id="razzTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--razz-accent)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="var(--razz-accent)" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {yTicks.map((tick) => {
              const y = pad.top + innerH - (tick / yMax) * innerH;
              return (
                <g key={`y-${tick}`}>
                  <line
                    className="razz-chart-grid"
                    x1={pad.left}
                    x2={width - pad.right}
                    y1={y}
                    y2={y}
                  />
                  <text className="razz-chart-axis" x={pad.left - 8} y={y + 4} textAnchor="end">
                    {formatNumber(tick)}
                  </text>
                </g>
              );
            })}

            <path className="razz-chart-area" d={areaPath} fill="url(#razzTrendFill)" />
            <path className="razz-chart-line" d={linePath} />

            {points.map((point) => {
              const barH = Math.max(point.value > 0 ? 4 : 0, (point.value / yMax) * innerH);
              const barY = pad.top + innerH - barH;
              const showTick = point.index % tickEvery === 0 || point.index === points.length - 1;
              const isHovered = hoverIndex === point.index;
              const isSelected = selectedDay === point.row.day;
              const isActive = isHovered || isSelected;
              return (
                <g
                  key={point.row.day}
                  onMouseEnter={() => setHoverIndex(point.index)}
                  onClick={() => onDaySelect?.(point.row.day)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onDaySelect?.(point.row.day);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={spanish
                    ? `Abrir detalle del ${formatChartDay(point.row.day, locale, 'full')}`
                    : `Open details for ${formatChartDay(point.row.day, locale, 'full')}`}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    className="razz-chart-hit"
                    x={point.x - step / 2}
                    y={pad.top}
                    width={step}
                    height={innerH}
                  />
                  <rect
                    className={`razz-chart-bar-svg${isActive ? ' is-active' : ''}${point.value === 0 ? ' is-zero' : ''}`}
                    x={point.x - barW / 2}
                    y={barY}
                    width={barW}
                    height={Math.max(barH, point.value === 0 ? 2 : 0)}
                    rx={4}
                  />
                  <circle
                    className={`razz-chart-dot${isActive ? ' is-active' : ''}`}
                    cx={point.x}
                    cy={point.y}
                    r={isActive ? 4.5 : 3}
                  />
                  {showTick ? (
                    <text
                      className="razz-chart-axis razz-chart-x"
                      x={point.x}
                      y={height - 12}
                      textAnchor="middle"
                    >
                      {formatChartDay(point.row.day, locale, 'tick')}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {active ? (
              <g className="razz-chart-tooltip" pointerEvents="none">
                <rect
                  x={Math.min(Math.max(active.x - 78, pad.left), width - pad.right - 156)}
                  y={Math.max(pad.top + 4, active.y - 52)}
                  width={156}
                  height={44}
                  rx={8}
                />
                <text
                  x={Math.min(Math.max(active.x, pad.left + 78), width - pad.right - 78)}
                  y={Math.max(pad.top + 22, active.y - 34)}
                  textAnchor="middle"
                >
                  {formatChartDay(active.row.day, locale, 'full')}
                </text>
                <text
                  className="razz-chart-tooltip-value"
                  x={Math.min(Math.max(active.x, pad.left + 78), width - pad.right - 78)}
                  y={Math.max(pad.top + 38, active.y - 18)}
                  textAnchor="middle"
                >
                  {`${formatNumber(active.value)} ${metricLabel.toLowerCase()}`}
                </text>
              </g>
            ) : null}
          </svg>
        </div>
      )}
    </section>
  );
};

const RazzmatazzTrafficPanel: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  language = 'en',
  host = '',
}) => {
  const spanish = language === 'es';
  const lockedHost = (host || '').trim().toLowerCase().replace(/^www\./, '');
  const [linkedSites, setLinkedSites] = React.useState<LinkedSite[]>([]);
  const [selectedLinkedHost, setSelectedLinkedHost] = React.useState('nextcompli.com');
  const [newSiteHost, setNewSiteHost] = React.useState('');
  const [sitesBusy, setSitesBusy] = React.useState(false);
  const [sitesNote, setSitesNote] = React.useState('');
  const [snippetSiteId, setSnippetSiteId] = React.useState<number | null>(null);
  const hostFilter = lockedHost || selectedLinkedHost;
  const [rangeId, setRangeId] = React.useState('7d');
  const [metric, setMetric] = React.useState<MetricKey>('visitors');
  const [data, setData] = React.useState<RazzmatazzAnalyticsOverview | null>(null);
  const [compare, setCompare] = React.useState<RazzmatazzAnalyticsOverview | null>(null);
  const [sessions, setSessions] = React.useState<SessionRow[]>([]);
  const [sessionsNote, setSessionsNote] = React.useState('');
  const [authenticityReport, setAuthenticityReport] = React.useState<{
    verdict_en?: string;
    verdict_es?: string;
    likely_human?: number;
    uncertain?: number;
    likely_bot_or_scrape?: number;
    human_share_pct?: number;
    channels?: Array<{ channel: string; sessions: number }>;
    reasons?: Array<{ reason: string; count: number }>;
  } | null>(null);
  const [sessionInteractions, setSessionInteractions] = React.useState<SessionInteraction[]>([]);
  const [viewMode, setViewMode] = React.useState<'graph' | 'details'>('graph');
  const [selectedSession, setSelectedSession] = React.useState<string | null>(null);
  /** Tick so “Xm ago” stays fresh without a full refresh. */
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  /** Hide crawlers / CI from the session list (default on — same as JFGoss Traffic humans). */
  const [hideBots, setHideBots] = React.useState(true);
  /** One row per visitor (visitor_id → IP → session). */
  const [uniqueVisitsOnly, setUniqueVisitsOnly] = React.useState(false);
  const [dayFilter, setDayFilter] = React.useState<string | null>(null);
  const [channelFilter, setChannelFilter] = React.useState<string | null>(null);
  const [paidOnly, setPaidOnly] = React.useState(false);
  const [actionNote, setActionNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [explainMetric, setExplainMetric] = React.useState<string | null>(null);
  const [explainBusy, setExplainBusy] = React.useState(false);
  const [explainError, setExplainError] = React.useState('');
  const [explainResult, setExplainResult] = React.useState<{
    metric: string;
    label: string;
    patterns: string[];
    antipatterns: string[];
    conversions: string[];
    plan: ExplainPlanStep[];
    tools: ExplainPlanStep[];
    window_class?: string;
    window_label?: string;
    window_summary?: string;
    brief: string;
    ai?: boolean;
  } | null>(null);
  const [excludeBusy, setExcludeBusy] = React.useState(false);
  const [excludeNote, setExcludeNote] = React.useState('');
  const [localExcluded, setLocalExcluded] = React.useState(() => isSiteTrafficExcludedLocally());
  const detailsSectionRef = React.useRef<HTMLElement | null>(null);

  const focusDetails = React.useCallback(() => {
    setViewMode('details');
    window.requestAnimationFrame(() => {
      detailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      detailsSectionRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const openDayDetails = React.useCallback((day: string) => {
    setDayFilter(day);
    setMetric('sessions');
    setActionNote(spanish
      ? `Detalle filtrado al ${day}.`
      : `Details filtered to ${day}.`);
    focusDetails();
  }, [focusDetails, spanish]);

  React.useEffect(() => {
    if (viewMode !== 'details') return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [viewMode]);

  const rangeQuery = React.useMemo(() => {
    const preset = RANGE_PRESETS.find((row) => row.id === rangeId) || RANGE_PRESETS.find((row) => row.id === '7d')!;
    return preset.resolve();
  }, [rangeId]);

  const explainArea = React.useCallback(async (metricKey: string) => {
    if (!accessToken || !gatewayBaseUrl) return;
    setExplainMetric(metricKey);
    setExplainBusy(true);
    setExplainError('');
    try {
      const humans = sessions.filter((row) => !isNonHumanSession(row));
      const bots = sessions.filter((row) => isNonHumanSession(row));
      const channelCounts = new Map<string, number>();
      const pathCounts = new Map<string, number>();
      let paid = 0;
      let internal = 0;
      let organic = 0;
      const visitorKeys = new Set<string>();
      const ipHits = new Map<string, number>();
      const exitCounts = new Map<string, number>();
      for (const row of humans) {
        const channel = String(row.channel || 'Direct');
        channelCounts.set(channel, (channelCounts.get(channel) || 0) + 1);
        const path = String(row.entry_path || '/');
        pathCounts.set(path, (pathCounts.get(path) || 0) + 1);
        const exit = String(row.exit_path || row.entry_path || '/');
        exitCounts.set(exit, (exitCounts.get(exit) || 0) + 1);
        if (sessionIsPaid(row)) paid += 1;
        const lowered = channel.toLowerCase();
        if (lowered.includes('internal')) internal += 1;
        if (lowered.includes('organic')) organic += 1;
        const visitor = String(row.visitor_id || '').trim();
        const ip = String(row.client_ip || '').trim();
        visitorKeys.add(visitor || (ip && ip !== '—' ? `ip:${ip}` : `s:${row.session_id}`));
        if (ip && ip !== '—') ipHits.set(ip, (ipHits.get(ip) || 0) + 1);
      }
      const topChannels = Array.from(channelCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([channel, count]) => ({ channel, sessions: count }));
      const topPaths = Array.from(pathCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([path, hits]) => ({ path, hits }));
      const topExits = Array.from(exitCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([path, hits]) => ({ path, hits }));
      const attention = (data as RazzmatazzAnalyticsOverview & {
        attention?: {
          summary?: Record<string, unknown>;
          where_eyeballs?: Array<Record<string, unknown>>;
          friction?: Array<Record<string, unknown>>;
        };
      })?.attention;
      const params = {
        metric: metricKey,
        hours: rangeQuery.hours,
        host: hostFilter,
        since: rangeQuery.since || '',
        until: rangeQuery.until || '',
        language: spanish ? 'es' : 'en',
        use_ai: true,
        audience: {
          human_sessions: humans.length,
          bot_sessions: bots.length,
          unique_visitors: visitorKeys.size,
          paid_sessions: paid,
          internal_sessions: internal,
          organic_sessions: organic,
          repeat_ips: Array.from(ipHits.values()).filter((n) => n >= 2).length,
          top_channels: topChannels,
          top_paths: topPaths,
          top_exits: topExits,
          attention_summary: attention?.summary || {},
          where_eyeballs: (attention?.where_eyeballs || []).slice(0, 8),
          friction: (attention?.friction || []).slice(0, 6),
          hide_bots: hideBots,
          unique_visits: uniqueVisitsOnly,
          paid_only: paidOnly,
          channel_filter: channelFilter || '',
          day_filter: dayFilter || '',
        },
      };
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/analytics/explain`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      const body = await response.json().catch(() => ({})) as {
        metric?: string;
        label?: string;
        patterns?: string[];
        antipatterns?: string[];
        conversions?: string[];
        plan?: ExplainPlanStep[];
        tools?: ExplainPlanStep[];
        window_class?: string;
        window_label?: string;
        window_summary?: string;
        brief?: string;
        ai?: boolean;
        detail?: string;
      };
      if (!response.ok) {
        setExplainError(typeof body.detail === 'string'
          ? body.detail
          : (spanish ? 'No se pudo explicar esta métrica.' : 'Could not explain this metric.'));
        setExplainResult(null);
        return;
      }
      const mapSteps = (rows: ExplainPlanStep[] | undefined): ExplainPlanStep[] => (
        Array.isArray(rows)
          ? rows.map((step) => ({
            when: String(step?.when || ''),
            title: String(step?.title || ''),
            detail: String(step?.detail || ''),
            action: String(step?.action || ''),
          })).filter((step) => Boolean(step.title))
          : []
      );
      setExplainResult({
        metric: String(body.metric || metricKey),
        label: String(body.label || metricKey),
        patterns: Array.isArray(body.patterns) ? body.patterns.map(String) : [],
        antipatterns: Array.isArray(body.antipatterns) ? body.antipatterns.map(String) : [],
        conversions: Array.isArray(body.conversions) ? body.conversions.map(String) : [],
        plan: mapSteps(body.plan),
        tools: mapSteps(body.tools),
        window_class: body.window_class ? String(body.window_class) : undefined,
        window_label: body.window_label ? String(body.window_label) : undefined,
        window_summary: body.window_summary ? String(body.window_summary) : undefined,
        brief: String(body.brief || ''),
        ai: Boolean(body.ai),
      });
    } catch {
      setExplainError(spanish ? 'No se pudo explicar esta métrica.' : 'Could not explain this metric.');
      setExplainResult(null);
    } finally {
      setExplainBusy(false);
    }
  }, [
    accessToken,
    channelFilter,
    data,
    dayFilter,
    gatewayBaseUrl,
    hideBots,
    hostFilter,
    paidOnly,
    rangeQuery.hours,
    rangeQuery.since,
    rangeQuery.until,
    sessions,
    spanish,
    uniqueVisitsOnly,
  ]);

  const loadSites = React.useCallback(async () => {
    if (!gatewayBaseUrl || !accessToken) return;
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/sites`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = (await response.json().catch(() => ({}))) as {
        sites?: LinkedSite[];
        detail?: string;
        note?: string;
      };
      if (!response.ok) {
        setSitesNote(String(body.detail || (spanish ? 'No se pudieron cargar los sitios.' : 'Could not load websites.')));
        return;
      }
      const sites = Array.isArray(body.sites) ? body.sites : [];
      setLinkedSites(sites);
      setSelectedLinkedHost((current) => {
        if (lockedHost || current) return current;
        const preferred = sites.find((site) => {
          const host = String(site.host || '').toLowerCase();
          return host === 'nextcompli.com' || host.endsWith('.nextcompli.com');
        });
        if (preferred?.host) return String(preferred.host);
        return sites[0]?.host ? String(sites[0].host) : 'nextcompli.com';
      });
      if (body.note) setSitesNote(body.note);
    } catch {
      setSitesNote(spanish ? 'No se pudieron cargar los sitios.' : 'Could not load websites.');
    }
  }, [accessToken, gatewayBaseUrl, lockedHost, spanish]);

  React.useEffect(() => {
    void loadSites();
  }, [loadSites]);

  const addLinkedSite = React.useCallback(async () => {
    const hostValue = newSiteHost.trim();
    if (!hostValue) return;
    setSitesBusy(true);
    setSitesNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/sites`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ host: hostValue }),
      });
      const body = (await response.json().catch(() => ({}))) as { site?: LinkedSite; detail?: string };
      if (!response.ok) {
        setSitesNote(String(body.detail || (spanish ? 'No se pudo vincular el sitio.' : 'Could not link website.')));
        return;
      }
      setNewSiteHost('');
      if (body.site?.host) setSelectedLinkedHost(body.site.host);
      if (body.site?.id) setSnippetSiteId(body.site.id);
      await loadSites();
      setSitesNote(
        spanish
          ? '¡Sitio conectado! Abra “Cómo instalar” y siga los clics de colores.'
          : 'Site connected! Open “How to install” and follow the colorful clicks.',
      );
    } catch {
      setSitesNote(spanish ? 'No se pudo vincular el sitio.' : 'Could not link website.');
    } finally {
      setSitesBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, loadSites, newSiteHost, spanish]);

  const load = React.useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const overviewParams = new URLSearchParams({ hours: String(rangeQuery.hours) });
      if (rangeQuery.since) overviewParams.set('since', rangeQuery.since);
      if (rangeQuery.until) overviewParams.set('until', rangeQuery.until);
      if (hostFilter) overviewParams.set('host', hostFilter);
      overviewParams.set('humans_only', 'true');

      // Previous equal-length window (Plausible “Previous period”).
      const endMs = rangeQuery.until ? Date.parse(rangeQuery.until) : Date.now();
      const startMs = rangeQuery.since
        ? Date.parse(rangeQuery.since)
        : endMs - rangeQuery.hours * 3_600_000;
      const span = Math.max(3_600_000, endMs - startMs);
      const prevUntil = new Date(startMs).toISOString();
      const prevSince = new Date(startMs - span).toISOString();
      const compareParams = new URLSearchParams({
        hours: String(Math.max(1, Math.round(span / 3_600_000))),
        since: prevSince,
        until: prevUntil,
        humans_only: 'true',
      });
      if (hostFilter) compareParams.set('host', hostFilter);

      const sessionHours = Math.min(8760, Math.max(1, rangeQuery.hours));
      const sessionParams = new URLSearchParams({
        hours: String(sessionHours),
        // High enough that Humans only / Unique visits can rebuild the chart from the same list.
        limit: '500',
      });
      if (rangeQuery.since) sessionParams.set('since', rangeQuery.since);
      if (rangeQuery.until) sessionParams.set('until', rangeQuery.until);
      if (hostFilter) sessionParams.set('host', hostFilter);
      const authenticityParams = new URLSearchParams(sessionParams);
      const [overviewRes, compareRes, sessionsRes, authenticityRes] = await Promise.all([
        fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/analytics/overview?${overviewParams}`, { headers }),
        fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/analytics/overview?${compareParams}`, { headers }),
        fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/analytics/sessions?${sessionParams}`, { headers }),
        fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/analytics/authenticity?${authenticityParams}`, { headers }),
      ]);
      const payload = (await overviewRes.json().catch(() => ({}))) as RazzmatazzAnalyticsOverview & {
        detail?: string;
      };
      if (!overviewRes.ok) {
        throw new Error(
          typeof payload.detail === 'string'
            ? payload.detail
            : spanish
              ? `No se pudo cargar el tráfico (${overviewRes.status}).`
              : `Could not load traffic (${overviewRes.status}).`,
        );
      }
      setData(payload);
      if (compareRes.ok) {
        setCompare((await compareRes.json().catch(() => null)) as RazzmatazzAnalyticsOverview | null);
      } else {
        setCompare(null);
      }
      if (sessionsRes.ok) {
        const sessionPayload = (await sessionsRes.json().catch(() => ({}))) as {
          sessions?: SessionRow[];
          privacy_note?: string;
        };
        setSessions(Array.isArray(sessionPayload.sessions) ? sessionPayload.sessions : []);
        setSessionsNote(String(sessionPayload.privacy_note || ''));
      } else {
        setSessions([]);
      }
      if (authenticityRes.ok) {
        setAuthenticityReport(await authenticityRes.json().catch(() => null));
      } else {
        setAuthenticityReport(null);
      }
    } catch (err) {
      setData(null);
      setCompare(null);
      setSessions([]);
      setAuthenticityReport(null);
      setError(err instanceof Error ? err.message : spanish ? 'Error de tráfico.' : 'Traffic error.');
    } finally {
      setBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, hostFilter, rangeQuery, spanish]);

  React.useEffect(() => {
    if (!selectedSession || !accessToken) {
      setSessionInteractions([]);
      return undefined;
    }
    let cancelled = false;
    const loadTimeline = async () => {
      try {
        const response = await fetch(
          `${gatewayBaseUrl}/api/v1/razzmatazz/analytics/sessions/${encodeURIComponent(selectedSession)}/timeline`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        const body = (await response.json().catch(() => ({}))) as { interactions?: SessionInteraction[] };
        if (!cancelled) {
          setSessionInteractions(Array.isArray(body.interactions) ? body.interactions : []);
        }
      } catch {
        if (!cancelled) setSessionInteractions([]);
      }
    };
    void loadTimeline();
    return () => {
      cancelled = true;
    };
  }, [accessToken, gatewayBaseUrl, selectedSession]);

  const excludeThisDevice = React.useCallback(async (includeIp = true) => {
    const visitor = getSiteVisitorId();
    if (!visitor) {
      setExcludeNote(spanish
        ? 'No hay visitor_id en este navegador.'
        : 'No visitor id in this browser yet.');
      return;
    }
    setExcludeBusy(true);
    setExcludeNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/site-traffic/exclusions/this-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          visitor_id: visitor,
          include_ip: includeIp,
          label: spanish ? 'Mi dispositivo' : 'My device',
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`);
      }
      setSiteTrafficExcludedLocally(true);
      setLocalExcluded(true);
      setExcludeNote(spanish
        ? 'Este dispositivo queda fuera de Traffic y de los digests.'
        : 'This device is excluded from Traffic and digests.');
      void load();
    } catch (err) {
      setExcludeNote(err instanceof Error ? err.message : (spanish ? 'No se pudo excluir' : 'Could not exclude'));
    } finally {
      setExcludeBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, load, spanish]);

  const excludeSessionIdentity = React.useCallback(async (row: SessionRow) => {
    const visitor = String(row.visitor_id || '').trim();
    const ip = String(row.client_ip || '').trim();
    if (!visitor && !ip) {
      setExcludeNote(spanish ? 'Sin visitor_id ni IP para excluir.' : 'No visitor id or IP to exclude.');
      return;
    }
    setExcludeBusy(true);
    setExcludeNote('');
    try {
      const posts: Array<Promise<Response>> = [];
      if (visitor) {
        posts.push(fetch(`${gatewayBaseUrl}/api/v1/site-traffic/exclusions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            kind: 'visitor_id',
            value: visitor,
            label: spanish ? 'Dispositivo de prueba' : 'Test device',
          }),
        }));
      }
      if (ip && ip !== '—') {
        posts.push(fetch(`${gatewayBaseUrl}/api/v1/site-traffic/exclusions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            kind: 'client_ip',
            value: ip,
            label: spanish ? 'IP de prueba' : 'Test IP',
          }),
        }));
      }
      const results = await Promise.all(posts);
      if (results.some((res) => !res.ok)) {
        throw new Error(spanish ? 'No se pudo excluir la sesión' : 'Could not exclude session');
      }
      setExcludeNote(spanish
        ? 'Sesión excluida de Traffic y digests.'
        : 'Session excluded from Traffic and digests.');
      void load();
    } catch (err) {
      setExcludeNote(err instanceof Error ? err.message : (spanish ? 'No se pudo excluir' : 'Could not exclude'));
    } finally {
      setExcludeBusy(false);
    }
  }, [accessToken, gatewayBaseUrl, load, spanish]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Keep Active now / Details fresh while the operator watches a live visit.
  React.useEffect(() => {
    if (viewMode !== 'details' && metric !== 'active') {
      return undefined;
    }
    const id = window.setInterval(() => {
      void load();
    }, 15_000);
    return () => window.clearInterval(id);
  }, [load, metric, viewMode]);

  const chartMetric: Exclude<MetricKey, 'active'> =
    metric === 'active' ? 'sessions' : metric;
  const botCount = sessions.filter((row) => isNonHumanSession(row)).length;
  const humanSessions = sessions.filter((row) => !isNonHumanSession(row));
  const audienceSessions = hideBots ? humanSessions : sessions;
  const channelFiltered = audienceSessions.filter((row) => {
    if (paidOnly) {
      return sessionIsPaid(row);
    }
    if (channelFilter) {
      return String(row.channel || '').toLowerCase() === channelFilter.toLowerCase();
    }
    return true;
  });
  // Graph + ranks use the toggles over the full range; day click only narrows Details.
  const graphAudience = channelFiltered;
  const graphCounted = uniqueVisitsOnly
    ? collapseUniqueSessions(graphAudience)
    : graphAudience;
  const dayFiltered = dayFilter
    ? channelFiltered.filter((row) => String(row.started_at || row.last_at || '').slice(0, 10) === dayFilter)
    : channelFiltered;
  const uniqueFiltered = uniqueVisitsOnly
    ? collapseUniqueSessions(dayFiltered)
    : dayFiltered;
  const clientFiltersActive = hideBots || uniqueVisitsOnly || paidOnly || Boolean(channelFilter);
  const scaffoldDays: DayMetricRow[] = (data?.by_day || []).map((row) => ({
    day: String(row.day || ''),
    pageviews: Number(row.pageviews || 0),
    sessions: Number(row.sessions || 0),
    visitors: Number(row.visitors || 0),
  }));
  const days = clientFiltersActive
    ? aggregateDaysFromSessions(scaffoldDays, graphAudience, uniqueVisitsOnly)
    : scaffoldDays;
  const filteredPathRanks = pathHitsFromSessions(graphCounted, 8);
  const filteredEntryRanks = rankFromSessions(graphCounted, (row) => row.entry_path || '/', 8);
  const filteredChannelRanks = rankFromSessions(graphCounted, (row) => row.channel || 'Direct', 12);
  const filteredReferrerRanks = rankFromSessions(
    graphCounted,
    (row) => row.landing_referrer || row.referrer || '(direct)',
    8,
  );
  const filteredCountryRanks = rankFromSessions(graphCounted, (row) => row.country || '—', 8);
  const filteredDeviceRanks = rankFromSessions(graphCounted, (row) => row.device || 'unknown', 10);
  const filteredBrowserRanks = rankFromSessions(graphCounted, (row) => row.browser || 'unknown', 10);
  const filteredUtmSources = rankFromSessions(
    graphCounted.filter((row) => String(row.utm_source || '').trim()),
    (row) => String(row.utm_source || ''),
    8,
  );
  const filteredUtmCampaigns = rankFromSessions(
    graphCounted.filter((row) => String(row.utm_campaign || '').trim()),
    (row) => String(row.utm_campaign || ''),
    8,
  );
  const utmSources = clientFiltersActive
    ? filteredUtmSources.map((row) => ({ source: row.label, hits: row.value }))
    : (data?.utm_sources || []);
  const utmCampaigns = clientFiltersActive
    ? filteredUtmCampaigns.map((row) => ({ campaign: row.label, hits: row.value }))
    : (data?.utm_campaigns || []);
  const filteredCompanyRanks = rankFromSessions(
    graphCounted.filter((row) => String(row.company?.org_name || '').trim()),
    (row) => String(row.company?.org_name || ''),
    8,
  );
  const companyRanks = clientFiltersActive
    ? filteredCompanyRanks
    : (data?.top_companies || []).map((row) => ({
      label: row.org_name,
      value: Number(row.sessions || 0),
    }));
  const identifiedInFilter = uniqueFiltered.filter((row) => Boolean(row.identity?.identified)).length;
  const identifiedCount = clientFiltersActive
    ? identifiedInFilter
    : Number(data?.identified_visitors || identifiedInFilter || 0);
  const activeSessions = uniqueFiltered.filter((row) => isActiveSession(row.last_at));
  const visibleSessions = metric === 'active' ? activeSessions : uniqueFiltered;

  const repeatVisitorLines = React.useMemo(() => {
    const counts = new Map<string, { ip: string; hits: number; paths: Set<string>; channel: string }>();
    for (const row of humanSessions) {
      const ip = String(row.client_ip || '').trim();
      if (!ip || ip === '—') continue;
      const current = counts.get(ip) || {
        ip,
        hits: 0,
        paths: new Set<string>(),
        channel: String(row.channel || ''),
      };
      current.hits += 1;
      if (row.entry_path) current.paths.add(row.entry_path);
      counts.set(ip, current);
    }
    return Array.from(counts.values())
      .filter((row) => row.hits >= 2)
      .sort((a, b) => b.hits - a.hits)
      .slice(0, 25)
      .map((row) => `${row.ip}\t${row.hits} sessions\t${row.channel || '—'}\t${Array.from(row.paths).slice(0, 3).join(', ')}`);
  }, [humanSessions]);

  const applyExplainAction = React.useCallback(async (action: string) => {
    setActionNote('');
    if (action === 'humans_unique') {
      setHideBots(true);
      setUniqueVisitsOnly(true);
      setPaidOnly(false);
      setChannelFilter(null);
      focusDetails();
      setActionNote(spanish
        ? 'Solo humanos · visitas únicas. Detalle abierto.'
        : 'Humans only · unique visits. Details open.');
      return;
    }
    if (action === 'hide_bots_details') {
      setHideBots(true);
      setPaidOnly(false);
      setChannelFilter(null);
      focusDetails();
      setActionNote(spanish
        ? 'Bots ocultos. Detalle abierto con visitas humanas.'
        : 'Bots hidden. Details open on human visits.');
      return;
    }
    if (action === 'paid_sessions') {
      setHideBots(true);
      setPaidOnly(true);
      setChannelFilter(null);
      setMetric('sessions');
      focusDetails();
      setActionNote(spanish
        ? 'Filtro: sesiones con UTM / canal de pago.'
        : 'Filter: sessions with UTM / paid channel.');
      return;
    }
    if (action === 'warm_internal') {
      setHideBots(true);
      setPaidOnly(false);
      setChannelFilter('Internal');
      setMetric('sessions');
      focusDetails();
      setActionNote(spanish
        ? 'Filtro: canal Internal (rutas cálidas).'
        : 'Filter: Internal channel (warm paths).');
      return;
    }
    if (action === 'copy_pursuit') {
      const text = repeatVisitorLines.length
        ? [
          spanish ? 'Leads de Pursuit (IPs repetidas)' : 'Pursuit leads (repeat IPs)',
          ...repeatVisitorLines,
        ].join('\n')
        : (spanish
          ? 'No hay IPs repetidas en esta ventana.'
          : 'No repeat IPs in this window.');
      try {
        await navigator.clipboard.writeText(text);
        setActionNote(spanish
          ? `Copiado ${repeatVisitorLines.length} IP(s) para Pursuit.`
          : `Copied ${repeatVisitorLines.length} IP(s) for Pursuit.`);
      } catch {
        setActionNote(spanish ? 'No se pudo copiar.' : 'Could not copy.');
      }
      return;
    }
    if (action === 'clear_filters') {
      setDayFilter(null);
      setChannelFilter(null);
      setPaidOnly(false);
      setUniqueVisitsOnly(false);
      setActionNote(spanish ? 'Filtros quitados.' : 'Filters cleared.');
    }
  }, [focusDetails, repeatVisitorLines, spanish]);
  const activeSession = visibleSessions.find((row) => row.session_id === selectedSession)
    || sessions.find((row) => row.session_id === selectedSession)
    || null;

  const visibleSessionKey = visibleSessions.map((row) => row.session_id).join('|');
  // Always show a session inspector when Details has rows (Matomo visitor-log pattern).
  React.useEffect(() => {
    if (viewMode !== 'details' || visibleSessions.length === 0) {
      return;
    }
    const stillVisible = Boolean(
      selectedSession && visibleSessions.some((row) => row.session_id === selectedSession),
    );
    if (!stillVisible) {
      setSelectedSession(visibleSessions[0].session_id);
    }
  }, [viewMode, visibleSessionKey, selectedSession, visibleSessions]);

  const deltaFor = (key: 'visitors' | 'sessions' | 'pageviews'): number | null => {
    if (!data || !compare) return null;
    return pctChange(Number(data[key] || 0), Number(compare[key] || 0));
  };

  // KPIs, graph, and ranks share the toggle filters (not raw overview totals with crawlers).
  const displayVisitors = clientFiltersActive
    ? new Set(graphAudience.map(sessionUniqueKey)).size
    : Number(data?.visitors ?? 0);
  const displaySessions = clientFiltersActive
    ? graphCounted.length
    : Number(data?.sessions ?? 0);
  const displayPageviews = clientFiltersActive
    ? graphAudience.reduce((sum, row) => sum + Number(row.pageviews || 0), 0)
    : Number(data?.pageviews ?? 0);
  const displayActive = clientFiltersActive
    ? graphCounted.filter((row) => isActiveSession(row.last_at)).length
    : Number(data?.active_now ?? 0);
  const displayBouncePct = (() => {
    if (!clientFiltersActive) return Number(data?.bounce_rate_pct ?? 0);
    if (!graphCounted.length) return 0;
    const bounced = graphCounted.filter((row) => Number(row.pageviews || 0) <= 1).length;
    return Math.round((bounced / graphCounted.length) * 1000) / 10;
  })();
  const displayAvgDuration = (() => {
    if (!clientFiltersActive) return Number(data?.avg_session_duration_s || 0);
    if (!graphCounted.length) return 0;
    const total = graphCounted.reduce((sum, row) => sum + Number(row.duration_s || 0), 0);
    return total / graphCounted.length;
  })();
  const displayPagesPerSession = (() => {
    if (!clientFiltersActive) return Number(data?.pages_per_session ?? 0);
    if (!graphCounted.length) return 0;
    return Math.round((displayPageviews / graphCounted.length) * 10) / 10;
  })();

  const kpis: Array<{
    key: string;
    label: string;
    value: string;
    help?: string;
    selectable?: MetricKey;
    delta?: number | null;
  }> = [
    {
      key: 'visitors',
      label: spanish ? 'Visitantes' : 'Visitors',
      value: formatNumber(displayVisitors),
      selectable: 'visitors',
      delta: clientFiltersActive ? null : deltaFor('visitors'),
      help: hideBots && botCount
        ? (spanish
          ? `Solo humanos. ${botCount} bot(s) ocultos — desactive «Solo humanos» para verlos. La gráfica usa el mismo filtro.`
          : `Humans only. ${botCount} bot(s) hidden — turn off Humans only to see them. The graph uses the same filter.`)
        : undefined,
    },
    {
      key: 'sessions',
      label: spanish ? 'Sesiones' : 'Sessions',
      value: formatNumber(displaySessions),
      selectable: 'sessions',
      delta: clientFiltersActive ? null : deltaFor('sessions'),
    },
    {
      key: 'pageviews',
      label: spanish ? 'Vistas' : 'Pageviews',
      value: formatNumber(displayPageviews),
      selectable: 'pageviews',
      delta: clientFiltersActive ? null : deltaFor('pageviews'),
    },
    {
      key: 'active',
      label: spanish ? 'Activos ahora' : 'Active now',
      value: formatNumber(displayActive),
      selectable: 'active',
      help: spanish
        ? 'Sesiones con actividad en los últimos 5 minutos. Pulsa para ver el detalle.'
        : 'Sessions with activity in the last 5 minutes. Click to open session details.',
    },
    {
      key: 'bounce',
      label: spanish ? 'Rebote' : 'Bounce',
      value: `${displayBouncePct}%`,
      help: spanish ? 'Sesiones de una sola página.' : 'Single-page sessions.',
    },
    {
      key: 'duration',
      label: spanish ? 'Duración media' : 'Avg. duration',
      value: formatDuration(displayAvgDuration),
    },
    {
      key: 'pps',
      label: spanish ? 'Páginas / sesión' : 'Pages / session',
      value: formatNumber(displayPagesPerSession),
    },
  ];

  const openSnippet = linkedSites.find((site) => site.id === snippetSiteId) || null;

  const trackingLabel = hostFilter || (spanish ? 'todos los sitios (elige uno)' : 'all sites (pick one)');

  return (
    <div className="razz-traffic">
      <div className="razz-tracking-banner" role="status">
        <div className="razz-tracking-banner-main">
          <span className="razz-tracking-dot" aria-hidden="true" />
          <div>
            <strong>{spanish ? 'Sitio en seguimiento' : 'Website being tracked'}</strong>
            <p>
              {hostFilter
                ? (spanish ? `Números de ${hostFilter} solamente.` : `Numbers are for ${hostFilter} only.`)
                : (spanish
                  ? 'Ahora mezcla todos los sitios. Elija un sitio abajo para ver números limpios.'
                  : 'This mixes every linked site. Pick one site below for clean numbers.')}
            </p>
          </div>
        </div>
        <code className="razz-tracking-host">{trackingLabel}</code>
      </div>
      {!lockedHost ? (
        <section className="razz-traffic-sites" aria-labelledby="razz-linked-sites-heading">
          {linkedSites.length === 0 ? (
            <div className="razz-traffic-sites-empty">
              <div className="razz-traffic-sites-empty-emoji" aria-hidden="true">🚀</div>
              <h4 id="razz-linked-sites-heading">
                {spanish ? 'Conecte su sitio web (paso 1 de 2)' : 'Connect your website (step 1 of 2)'}
              </h4>
              <p>
                {spanish
                  ? 'Escriba la dirección de su sitio (como midulceria.com). Luego le mostramos clics sencillos para pegar un “timbre” de visitas. No hace falta saber programar.'
                  : 'Type your website address (like mybakery.com). Then we show easy clicks to paste a visit “doorbell.” No coding class needed.'}
              </p>
            </div>
          ) : (
            <h4 id="razz-linked-sites-heading">
              {spanish ? 'Sitios web vinculados' : 'Linked websites'}
              <HelpBadge label={spanish ? 'Cómo se vincula un sitio' : 'How website linking works'}>
                <p>
                  {spanish
                    ? 'Registre el dominio y pegue el código. Escaneamos el sitio para decirle si es WordPress, Wix, Shopify u otro. No es el agente de cumplimiento.'
                    : 'Register the domain and paste the code. We scan the site to tell you if it is WordPress, Wix, Shopify, or something else. This is not the compliance agent.'}
                </p>
              </HelpBadge>
            </h4>
          )}
          <div className="razz-traffic-sites-row">
            {linkedSites.length > 0 ? (
              <label>
                <span className="visually-hidden">{spanish ? 'Sitio activo' : 'Active site'}</span>
                <select
                  value={selectedLinkedHost}
                  onChange={(event) => setSelectedLinkedHost(event.target.value)}
                >
                  <option value="">{spanish ? 'Todos los sitios' : 'All sites'}</option>
                  {linkedSites.map((site) => (
                    <option key={site.id} value={site.host}>
                      {site.label || site.host}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="razz-traffic-sites-add">
              <span className="visually-hidden">{spanish ? 'Dominio' : 'Domain'}</span>
              <input
                type="text"
                value={newSiteHost}
                onChange={(event) => setNewSiteHost(event.target.value)}
                placeholder="www.mybakery.com"
                autoComplete="url"
              />
            </label>
            <button type="button" className="dashboard-btn" disabled={sitesBusy || !newSiteHost.trim()} onClick={() => void addLinkedSite()}>
              {sitesBusy
                ? (spanish ? 'Conectando…' : 'Connecting…')
                : spanish
                  ? '🔗 Conectar mi sitio'
                  : '🔗 Connect my site'}
            </button>
            {linkedSites.map((site) => (
              <button
                key={`snip-${site.id}`}
                type="button"
                className={`dashboard-btn dashboard-btn-ghost${snippetSiteId === site.id ? ' is-active' : ''}`}
                onClick={() => setSnippetSiteId(site.id === snippetSiteId ? null : site.id)}
              >
                {spanish ? `📖 Cómo instalar · ${site.host}` : `📖 How to install · ${site.host}`}
              </button>
            ))}
          </div>
          {openSnippet?.snippet ? (
            <RazzmatazzInstallGuide
              language={spanish ? 'es' : 'en'}
              snippet={openSnippet.snippet}
              host={openSnippet.host}
              siteId={openSnippet.id}
              gatewayBaseUrl={gatewayBaseUrl}
              accessToken={accessToken}
            />
          ) : null}
          {sitesNote ? <p className="razz-muted" role="status">{sitesNote}</p> : null}
        </section>
      ) : null}
      <div className="razz-traffic-toolbar">
        <div className="razz-traffic-title-block">
          <div className="razz-traffic-title-row">
            <h3>
              {spanish ? 'Tráfico del sitio' : 'Site traffic'}
              {hostFilter ? (
                <span className="razz-muted" style={{ fontWeight: 500, marginLeft: '0.45rem', fontSize: '0.85em' }}>
                  · {hostFilter}
                </span>
              ) : null}
              <HelpBadge label={spanish ? 'Qué mide Razzmatazz' : 'What Razzmatazz measures'}>
                <p>
                  {spanish
                    ? hostFilter
                      ? `Tráfico de marketing de ${hostFilter}. No hay Google Analytics. /console y /embed no cuentan.`
                      : 'Tráfico de marketing de primera parte. No hay Google Analytics. /console no cuenta. Correo diario y textos críticos: pestaña Correo y textos.'
                    : hostFilter
                      ? `First-party marketing traffic for ${hostFilter}. No Google Analytics. /console and /embed are not counted.`
                      : 'First-party marketing traffic. No Google Analytics. /console is not counted. Daily email and critical texts live under Email & texts.'}
                </p>
              </HelpBadge>
            </h3>
            <div
              className="razz-view-toggle razz-view-toggle--hero"
              role="tablist"
              aria-label={spanish ? 'Vista de tráfico' : 'Traffic view'}
            >
              <button
                type="button"
                role="tab"
                id="razz-view-graph"
                aria-controls="razz-traffic-main"
                aria-selected={viewMode === 'graph'}
                className={viewMode === 'graph' ? 'is-active' : undefined}
                onClick={() => setViewMode('graph')}
              >
                {spanish ? 'Gráfica' : 'Graph'}
              </button>
              <button
                type="button"
                role="tab"
                id="razz-view-details"
                aria-controls="razz-traffic-main"
                aria-selected={viewMode === 'details'}
                className={viewMode === 'details' ? 'is-active' : undefined}
                onClick={() => setViewMode('details')}
              >
                {spanish ? 'Detalle' : 'Details'}
              </button>
            </div>
          </div>
          <p className="razz-muted">
            {viewMode === 'graph'
              ? (spanish
                ? 'Solo humanos y Visitas únicas filtran la gráfica, los rangos y el detalle. Pulsa Visitantes, Sesiones o Vistas para la tendencia.'
                : 'Humans only and Unique visits filter the graph, rank lists, and details. Click Visitors, Sessions, or Pageviews for the trend.')
              : (spanish
                ? 'Mismos filtros que la gráfica. Sesiones con país, dispositivo, canal y recorrido.'
                : 'Same filters as the graph. Sessions with country, device, channel, and path trail.')}
          </p>
        </div>
        <div className="razz-traffic-controls">
          <label htmlFor="razz-traffic-window">
            {spanish ? 'Periodo' : 'Range'}
            <select
              id="razz-traffic-window"
              value={rangeId}
              onChange={(event) => setRangeId(event.target.value)}
            >
              {RANGE_PRESETS.map((option) => (
                <option key={option.id} value={option.id}>
                  {spanish ? option.es : option.en}
                </option>
              ))}
            </select>
          </label>
          <label className="razz-traffic-bots-toggle" htmlFor="razz-traffic-humans-only">
            <input
              id="razz-traffic-humans-only"
              type="checkbox"
              checked={hideBots}
              onChange={(event) => setHideBots(event.target.checked)}
            />
            <span>
              {spanish
                ? `Solo humanos${botCount ? ` · ${botCount} bot(s)` : ''}`
                : `Humans only${botCount ? ` · ${botCount} bot(s)` : ''}`}
            </span>
          </label>
          <label className="razz-traffic-bots-toggle" htmlFor="razz-traffic-unique-visits">
            <input
              id="razz-traffic-unique-visits"
              type="checkbox"
              checked={uniqueVisitsOnly}
              onChange={(event) => setUniqueVisitsOnly(event.target.checked)}
            />
            <span>{spanish ? 'Visitas únicas' : 'Unique visits'}</span>
          </label>
          <button
            type="button"
            className={`dashboard-btn razz-traffic-refresh${busy ? ' is-busy' : ''}`}
            disabled={busy}
            aria-label={spanish ? 'Actualizar tráfico' : 'Refresh traffic'}
            title={spanish ? 'Actualizar ahora' : 'Refresh now'}
            onClick={() => void load()}
          >
            <span className={`razz-traffic-refresh__icon${busy ? ' is-spin' : ''}`} aria-hidden="true">↻</span>
            {spanish ? 'Actualizar' : 'Refresh'}
          </button>
          <button
            type="button"
            className="dashboard-btn"
            disabled={excludeBusy || localExcluded}
            title={spanish
              ? 'Usa el visitor_id de este navegador + tu IP pública (nunca la MAC).'
              : 'Uses this browser’s visitor_id cookie + your public IP (never MAC address).'}
            onClick={() => void excludeThisDevice(true)}
          >
            {localExcluded
              ? (spanish ? 'Dispositivo excluido' : 'Device excluded')
              : (spanish ? 'Excluir mi dispositivo' : 'Exclude my device')}
          </button>
        </div>
      </div>

      {excludeNote ? <p className="razz-muted" role="status">{excludeNote}</p> : null}
      {error ? <p className="razz-error" role="alert">{error}</p> : null}

      <div
        id="razz-traffic-main"
        role="tabpanel"
        aria-labelledby={viewMode === 'graph' ? 'razz-view-graph' : 'razz-view-details'}
      >
      <div className="razz-kpi-strip" role="group" aria-label={spanish ? 'Indicadores' : 'Key metrics'}>
        {kpis.map((kpi) => {
          const selected = kpi.selectable === metric;
          const aiOpen = explainMetric === kpi.key;
          const aiButton = (
            <button
              type="button"
              className={`razz-kpi-ai${aiOpen ? ' is-open' : ''}${explainBusy && aiOpen ? ' is-busy' : ''}`}
              aria-pressed={aiOpen}
              aria-label={spanish
                ? `Analizar ${kpi.label} con IA y armar un plan`
                : `Analyze ${kpi.label} with AI and build a plan`}
              title={spanish
                ? 'IA: analiza visitas y propone acciones de conversión'
                : 'AI: analyze visits and propose conversion actions'}
              disabled={explainBusy}
              onClick={(event) => {
                event.stopPropagation();
                if (aiOpen && explainResult?.metric === kpi.key) {
                  setExplainMetric(null);
                  setExplainResult(null);
                  setExplainError('');
                  return;
                }
                void explainArea(kpi.key);
              }}
            >
              {explainBusy && aiOpen ? '…' : (spanish ? 'Analizar' : 'Analyze')}
            </button>
          );
          if (kpi.selectable) {
            return (
              <div key={kpi.key} className={`razz-kpi-card${selected ? ' is-selected' : ''}`}>
                <button
                  type="button"
                  className={`razz-kpi is-selectable${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  title={kpi.help || undefined}
                  onClick={() => {
                    setMetric(kpi.selectable!);
                    if (kpi.selectable === 'sessions' || kpi.selectable === 'active') {
                      setViewMode('details');
                    } else {
                      setViewMode('graph');
                    }
                  }}
                >
                  <span className="razz-kpi-label">{kpi.label}</span>
                  <strong className="razz-kpi-value">{kpi.value}</strong>
                  {kpi.delta != null ? (
                    <span className={`razz-kpi-delta${kpi.delta >= 0 ? ' is-up' : ' is-down'}`}>
                      {kpi.delta >= 0 ? '+' : ''}{kpi.delta}%
                    </span>
                  ) : null}
                </button>
                {aiButton}
              </div>
            );
          }
          return (
            <div key={kpi.key} className="razz-kpi-card">
              <div className="razz-kpi">
                <span className="razz-kpi-label">
                  {kpi.label}
                  {kpi.help ? (
                    <HelpBadge label={kpi.label}>
                      <p>{kpi.help}</p>
                    </HelpBadge>
                  ) : null}
                </span>
                <strong className="razz-kpi-value">{kpi.value}</strong>
              </div>
              {aiButton}
            </div>
          );
        })}
      </div>

      {explainMetric || explainResult || explainError ? (
        <aside
          className="razz-kpi-explain"
          role="region"
          aria-label={spanish ? 'La IA usa estos datos' : 'AI using this data'}
          ref={(node) => {
            if (node && (explainBusy || explainResult || explainError)) {
              node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }}
        >
          <header className="razz-kpi-explain-head">
            <div>
              <p className="razz-pref-kicker">
                {explainResult?.ai
                  ? (spanish ? 'IA · análisis de conversión' : 'AI · conversion analysis')
                  : (spanish ? 'Análisis · plan de acción' : 'Analysis · action plan')}
              </p>
              <h4>
                {explainResult?.label
                  || kpis.find((row) => row.key === explainMetric)?.label
                  || (spanish ? 'Métrica' : 'Metric')}
              </h4>
            </div>
            <button
              type="button"
              className="dashboard-btn"
              onClick={() => {
                setExplainMetric(null);
                setExplainResult(null);
                setExplainError('');
              }}
            >
              {spanish ? 'Cerrar' : 'Close'}
            </button>
          </header>
          {explainBusy ? (
            <p className="razz-muted" role="status">
              {spanish
                ? 'La IA está leyendo visitas, atención y fricción para armar un plan…'
                : 'AI is reading visits, attention, and friction to build a plan…'}
            </p>
          ) : null}
          {explainError ? <p className="razz-error" role="alert">{explainError}</p> : null}
          {explainResult ? (
            <div className="razz-kpi-explain-body">
              {explainResult.window_label ? (
                <p className="razz-kpi-explain-window" role="status">
                  <strong>{explainResult.window_label}</strong>
                  {explainResult.window_summary ? ` — ${explainResult.window_summary}` : ''}
                  {explainResult.ai
                    ? (spanish ? ' · con IA' : ' · with AI')
                    : (spanish ? ' · plan de respaldo' : ' · fallback plan')}
                </p>
              ) : null}
              {explainResult.brief ? (
                <p className="razz-kpi-explain-brief">{explainResult.brief}</p>
              ) : null}
              <h5 className="razz-kpi-explain-plan-label">
                {spanish ? 'Plan de acción' : 'Action plan'}
              </h5>
              <ol className="razz-kpi-explain-plan" aria-label={spanish ? 'Plan de acción' : 'Action plan'}>
                {(explainResult.plan.length
                  ? explainResult.plan
                  : explainResult.conversions.map((title) => ({
                    when: 'now',
                    title,
                    detail: '',
                    action: '',
                  }))
                ).map((step, index) => {
                  const whenLabel = step.when === 'this_week'
                    ? (spanish ? 'Esta semana' : 'This week')
                    : step.when === 'ignore'
                      ? (spanish ? 'Ignorar' : 'Ignore')
                      : (spanish ? 'Ahora' : 'Do now');
                  const hasAction = Boolean(step.action);
                  const content = (
                    <>
                      <span className={`razz-kpi-plan-when is-${step.when || 'now'}`}>{whenLabel}</span>
                      <strong>{step.title}</strong>
                      {step.detail && step.detail !== step.title ? <span>{step.detail}</span> : null}
                    </>
                  );
                  return (
                    <li key={`${step.when}-${step.title}-${index}`}>
                      {hasAction ? (
                        <button
                          type="button"
                          className={`razz-kpi-action${step.when === 'ignore' ? ' is-muted' : ''}`}
                          onClick={() => void applyExplainAction(String(step.action))}
                        >
                          {content}
                        </button>
                      ) : (
                        <div className={`razz-kpi-action is-static${step.when === 'ignore' ? ' is-muted' : ''}`}>
                          {content}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
              {explainResult.tools.length ? (
                <details className="razz-kpi-explain-tools">
                  <summary>{spanish ? 'Atajos de consola (filtros)' : 'Console shortcuts (filters)'}</summary>
                  <ul className="razz-kpi-explain-tools-list">
                    {explainResult.tools.map((step, index) => (
                      <li key={`tool-${step.action}-${index}`}>
                        <button
                          type="button"
                          className="razz-kpi-action is-muted"
                          onClick={() => void applyExplainAction(String(step.action))}
                        >
                          <strong>{step.title}</strong>
                          {step.detail ? <span>{step.detail}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              {(dayFilter || channelFilter || paidOnly || uniqueVisitsOnly) ? (
                <div className="razz-kpi-explain-actions">
                  <button
                    type="button"
                    className="razz-kpi-action is-muted"
                    onClick={() => void applyExplainAction('clear_filters')}
                  >
                    <strong>{spanish ? 'Quitar filtros' : 'Clear filters'}</strong>
                    <span>{spanish ? 'Volver a toda la ventana' : 'Back to the full window'}</span>
                  </button>
                </div>
              ) : null}
              {actionNote ? <p className="razz-kpi-explain-done" role="status">{actionNote}</p> : null}
              <details className="razz-kpi-explain-more">
                <summary>{spanish ? 'Ver señales' : 'See signals'}</summary>
                <div className="razz-kpi-explain-cols">
                  <section>
                    <h5>{spanish ? 'Patrones' : 'Patterns'}</h5>
                    <ul>
                      {(explainResult.patterns.length ? explainResult.patterns : [
                        spanish ? 'Sin señales fuertes aún.' : 'No strong signals yet.',
                      ]).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </section>
                  <section>
                    <h5>{spanish ? 'Riesgos' : 'Risks'}</h5>
                    <ul>
                      {(explainResult.antipatterns.length ? explainResult.antipatterns : [
                        spanish ? 'Ninguno marcado en esta ventana.' : 'None flagged in this window.',
                      ]).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </section>
                </div>
              </details>
            </div>
          ) : null}
        </aside>
      ) : null}

      {viewMode === 'graph' ? (
        <>
          {days.length > 0 && metric !== 'active' ? (
            <DayChart
              days={days}
              metric={chartMetric}
              spanish={spanish}
              selectedDay={dayFilter}
              onDaySelect={openDayDetails}
            />
          ) : null}
          {metric === 'active' ? (
            <p className="razz-muted razz-active-hint" role="status">
              {spanish
                ? `${activeSessions.length} activas — cambia a Detalle para verlas.`
                : `${activeSessions.length} active — switch to Details to inspect them.`}
            </p>
          ) : null}
          {(data?.attention?.where_eyeballs?.length || data?.attention?.friction?.length) ? (
            <section className="razz-attention" aria-label={spanish ? 'Atención y fricción' : 'Attention and friction'}>
              <header className="razz-attention-head">
                <div>
                  <h3>{spanish ? 'Dónde miraron' : 'Where eyeballs were'}</h3>
                  <p className="razz-muted">
                    {spanish
                      ? 'Tiempo visible por página (beacon o hueco entre vistas). Use esto para arreglar copia/CTA antes de pagar más clics.'
                      : 'Visible time per page (beacon or gap between views). Use this to fix copy/CTA before buying more clicks.'}
                  </p>
                </div>
              </header>
              <div className="razz-attention-grid">
                <RankList
                  title={spanish ? 'Más atención' : 'Most attention'}
                  empty={spanish ? 'Sin tiempos aún.' : 'No dwell times yet.'}
                  rows={(data?.attention?.where_eyeballs || []).slice(0, 8).map((row) => ({
                    label: `${String(row.path || '/')} · ${Number(row.avg_dwell_s || 0)}s`,
                    value: Number(row.views || 0),
                  }))}
                />
                <RankList
                  title={spanish ? 'Fricción / salida confusa' : 'Friction / confused exit'}
                  empty={spanish ? 'Sin fricción marcada.' : 'No friction flagged.'}
                  rows={(data?.attention?.friction || []).slice(0, 8).map((row) => ({
                    label: `${String(row.path || '/')} · ${Number(row.confused_exits || 0)} exits`,
                    value: Number(row.confused_exits || row.views || 0),
                  }))}
                />
              </div>
            </section>
          ) : null}
          {authenticityReport ? (
            <section className="razz-authenticity-card" aria-labelledby="razz-auth-heading">
              <h4 id="razz-auth-heading">
                {spanish ? '¿Son visitas reales?' : 'Are these real people?'}
                <HelpBadge label={spanish ? 'Cómo lo sabemos' : 'How we know'}>
                  <p>
                    {spanish
                      ? 'Miramos canal interno, tiempo de lectura cero, ráfagas en el mismo segundo y Chrome en Linux sin dwell. No es magia: es un detector de scrapes.'
                      : 'We look for Internal channel, zero reading time, same-second bursts, and Linux Chrome with no dwell. Not magic — scrape detection.'}
                  </p>
                </HelpBadge>
              </h4>
              <p className="razz-authenticity-verdict">
                {spanish ? authenticityReport.verdict_es : authenticityReport.verdict_en}
              </p>
              <div className="razz-authenticity-stats">
                <div><strong>{authenticityReport.likely_human ?? 0}</strong><span>{spanish ? 'Humanas' : 'Human'}</span></div>
                <div><strong>{authenticityReport.uncertain ?? 0}</strong><span>{spanish ? 'Dudosas' : 'Uncertain'}</span></div>
                <div><strong>{authenticityReport.likely_bot_or_scrape ?? 0}</strong><span>{spanish ? 'Bots / scrapes' : 'Bots / scrapes'}</span></div>
                <div><strong>{authenticityReport.human_share_pct ?? 0}%</strong><span>{spanish ? 'Humanas %' : 'Human %'}</span></div>
              </div>
            </section>
          ) : null}
          <p className="razz-geo-honesty razz-muted">
            {spanish
              ? (data?.geo_note
                || 'Ubicación aproximada: país, a veces región/ciudad. No hay barrio ni casa — eso no se puede saber con una IP.')
              : (data?.geo_note
                || 'Approximate location: country, sometimes region/city. No neighborhood or house — an IP cannot honestly provide that.')}
            {data?.duration_note ? ` ${data.duration_note}` : ''}
          </p>
          <div className="razz-rank-grid razz-rank-grid-dense">
            <RankList
              title={spanish ? 'Páginas' : 'Pages'}
              empty={spanish ? 'Aún no hay vistas.' : 'No pageviews yet.'}
              rows={clientFiltersActive
                ? filteredPathRanks
                : (data?.top_paths || []).slice(0, 8).map((row) => ({ label: row.path, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Entradas' : 'Entries'}
              empty={spanish ? 'Sin entradas.' : 'No entries yet.'}
              rows={clientFiltersActive
                ? filteredEntryRanks
                : (data?.entry_paths || []).slice(0, 8).map((row) => ({ label: row.path, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Canales' : 'Channels'}
              empty={spanish ? 'Sin canales.' : 'No channels yet.'}
              rows={clientFiltersActive
                ? filteredChannelRanks
                : (data?.top_channels || []).map((row) => ({ label: row.channel, value: row.sessions }))}
            />
            <RankList
              title={spanish ? 'Referencias' : 'Referrers'}
              empty={spanish ? 'Sin referencias.' : 'No referrers yet.'}
              rows={clientFiltersActive
                ? filteredReferrerRanks
                : (data?.top_referrers || []).slice(0, 8).map((row) => ({ label: row.referrer, value: row.sessions }))}
            />
            <RankList
              title={spanish ? 'Países' : 'Countries'}
              empty={spanish ? 'Sin país aún (cabecera CDN).' : 'No country yet (CDN header).'}
              rows={clientFiltersActive
                ? filteredCountryRanks
                : (data?.countries || []).slice(0, 8).map((row) => ({ label: row.country, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Regiones / estados' : 'Regions / states'}
              empty={spanish ? 'Sin región aún.' : 'No region yet.'}
              rows={(data?.regions || []).slice(0, 8).map((row) => ({ label: row.region, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Ciudades' : 'Cities'}
              empty={spanish ? 'Sin ciudad aún.' : 'No city yet.'}
              rows={(data?.cities || []).slice(0, 8).map((row) => ({ label: row.city, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Sitios (hosts)' : 'Sites (hosts)'}
              empty={spanish ? 'Sin hosts.' : 'No hosts yet.'}
              rows={(data?.hosts || []).slice(0, 8).map((row) => ({ label: row.host, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Dispositivos' : 'Devices'}
              empty={spanish ? 'Sin dispositivos.' : 'No devices yet.'}
              rows={clientFiltersActive
                ? filteredDeviceRanks
                : (data?.devices || []).map((row) => ({ label: row.device, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Navegadores' : 'Browsers'}
              empty={spanish ? 'Sin navegadores.' : 'No browsers yet.'}
              rows={clientFiltersActive
                ? filteredBrowserRanks
                : (data?.browsers || []).map((row) => ({ label: row.browser, value: row.hits }))}
            />
            <RankList
              title={
                <>
                  {spanish ? 'UTM' : 'UTM'}{' '}
                  <HelpBadge label="UTM">
                    <p>
                      {spanish
                        ? 'Códigos en el enlace (utm_source…) que dicen de dónde vino la visita.'
                        : 'Tags on the link (utm_source…) that say where the visit came from.'}
                    </p>
                  </HelpBadge>
                </>
              }
              empty={spanish
                ? 'Sin utm_source. Etiquete enlaces de pago/email.'
                : 'No utm_source yet. Tag paid/email links.'}
              rows={utmSources.slice(0, 8).map((row) => ({ label: row.source, value: row.hits }))}
            />
            <RankList
              title={spanish ? 'Campañas' : 'Campaigns'}
              empty={spanish
                ? 'Sin utm_campaign en esta ventana.'
                : 'No utm_campaign in this window.'}
              rows={utmCampaigns.slice(0, 8).map((row) => ({ label: row.campaign, value: row.hits }))}
            />
            <RankList
              title={
                <>
                  {spanish ? 'Empresas (IP)' : 'Companies (IP)'}{' '}
                  <HelpBadge label={spanish ? 'Empresa' : 'Company'}>
                    <p>
                      {spanish
                        ? 'Organización de red desde la IP (ASN/org). No es una persona nombrada. ISP de casa suele no resolver.'
                        : 'Network organization from connecting IP (ASN/org). Not a named person. Home ISP traffic often will not resolve.'}
                    </p>
                  </HelpBadge>
                </>
              }
              empty={spanish ? 'Sin coincidencias de empresa aún.' : 'No company matches yet.'}
              rows={companyRanks.slice(0, 8)}
            />
            <RankList
              title={spanish ? 'Identificados (1ª parte)' : 'Identified (1st party)'}
              empty={spanish
                ? 'Nadie se identificó (formulario / alta / login).'
                : 'Nobody identified yet (form / signup / login).'}
              rows={identifiedCount > 0
                ? [{ label: spanish ? 'Visitantes con email/nombre' : 'Visitors with email/name', value: identifiedCount }]
                : []}
            />
          </div>
        </>
      ) : (
        <section
          ref={detailsSectionRef}
          tabIndex={-1}
          className="razz-sessions razz-sessions--details"
          aria-label={
            metric === 'active'
              ? (spanish ? 'Sesiones activas ahora' : 'Sessions active now')
              : (spanish ? 'Detalle de sesiones' : 'Session details')
          }
        >
          <div className="razz-details-head">
            <div>
              <h3>{spanish ? 'Sesiones' : 'Sessions'}</h3>
              <p className="razz-muted">
                {metric === 'active'
                  ? (spanish
                    ? `${visibleSessions.length} con actividad en los últimos 5 minutos.`
                    : `${visibleSessions.length} with activity in the last 5 minutes.`)
                  : (spanish
                    ? `${visibleSessions.length} en este periodo${hideBots && botCount ? ` · ${botCount} bot(s) ocultos` : ''}${uniqueVisitsOnly ? ' · visitas únicas' : ''}${dayFilter ? ` · ${dayFilter}` : ''}${paidOnly ? ' · pago/UTM' : ''}${channelFilter ? ` · ${channelFilter}` : ''} · IP, dispositivo, referencia y recorrido.`
                    : `${visibleSessions.length} in this range${hideBots && botCount ? ` · ${botCount} bot(s) hidden` : ''}${uniqueVisitsOnly ? ' · unique visits' : ''}${dayFilter ? ` · ${dayFilter}` : ''}${paidOnly ? ' · paid/UTM' : ''}${channelFilter ? ` · ${channelFilter}` : ''} · IP, device, referrer, and path trail.`)}
              </p>
            </div>
            <div className="razz-details-head-actions">
              <button
                type="button"
                className={`dashboard-btn razz-kpi-ai-inline${explainMetric === 'sessions_list' ? ' is-open' : ''}`}
                disabled={explainBusy}
                aria-pressed={explainMetric === 'sessions_list'}
                title={spanish
                  ? 'IA: analiza visitas y propone acciones de conversión'
                  : 'AI: analyze visits and propose conversion actions'}
                onClick={() => {
                  if (explainMetric === 'sessions_list' && explainResult?.metric === 'sessions_list') {
                    setExplainMetric(null);
                    setExplainResult(null);
                    setExplainError('');
                    return;
                  }
                  void explainArea('sessions_list');
                }}
              >
                {explainBusy && explainMetric === 'sessions_list'
                  ? '…'
                  : (spanish ? 'Analizar sesiones' : 'Analyze sessions')}
              </button>
              {botCount > 0 ? (
                <button
                  type="button"
                  className={`dashboard-btn${hideBots ? '' : ' is-active'}`}
                  aria-pressed={!hideBots}
                  onClick={() => setHideBots((current) => !current)}
                >
                  {hideBots
                    ? (spanish ? `Mostrar bots (${botCount})` : `Show bots (${botCount})`)
                    : (spanish ? 'Ocultar bots' : 'Hide bots')}
                </button>
              ) : null}
              {(dayFilter || channelFilter || paidOnly) ? (
                <button
                  type="button"
                  className="dashboard-btn"
                  onClick={() => void applyExplainAction('clear_filters')}
                >
                  {spanish ? 'Quitar filtros' : 'Clear filters'}
                </button>
              ) : null}
              {metric === 'active' ? (
                <button type="button" className="dashboard-btn" onClick={() => setMetric('sessions')}>
                  {spanish ? 'Ver todas' : 'Show all'}
                </button>
              ) : null}
            </div>
          </div>
          {actionNote && viewMode === 'details' ? (
            <p className="razz-muted razz-sessions-note" role="status">{actionNote}</p>
          ) : null}
          {sessionsNote ? <p className="razz-muted razz-sessions-note">{sessionsNote}</p> : null}
          {visibleSessions.length === 0 ? (
            <p className="razz-muted">
              {metric === 'active'
                ? (spanish ? 'Nadie activo en este momento.' : 'No one is active right now.')
                : hideBots && botCount > 0
                  ? (spanish
                    ? `0 humanos en este periodo · ${botCount} bot(s) ocultos. Pulse «Mostrar bots» o desactive Solo humanos.`
                    : `0 humans in this range · ${botCount} bot(s) hidden. Click Show bots or turn off Humans only.`)
                  : (spanish ? 'Aún no hay sesiones en este periodo.' : 'No sessions in this range yet.')}
            </p>
          ) : (
            <div className="razz-sessions-layout">
              <ul className="razz-session-list">
                {visibleSessions.map((row) => {
                  const selected = row.session_id === selectedSession;
                  const live = isActiveSession(row.last_at);
                  return (
                    <li key={row.session_id}>
                      <button
                        type="button"
                        className={`razz-session-row${selected ? ' is-selected' : ''}${live ? ' is-live' : ''}`}
                        onClick={() => setSelectedSession(row.session_id)}
                      >
                        <span className="razz-session-main">
                          <strong>
                            {live ? <span className="razz-live-dot" aria-hidden /> : null}
                            {row.entry_path || '/'}
                            {row.exit_path && row.exit_path !== row.entry_path ? ` → ${row.exit_path}` : ''}
                            {row.is_bot ? (
                              <span className="razz-session-bot-tag">
                                {row.bot_name || (spanish ? 'Bot' : 'Bot')}
                              </span>
                            ) : null}
                          </strong>
                          <span className="razz-muted">
                            {[
                              row.identity?.identified
                                ? (row.identity.email || row.identity.full_name || (spanish ? 'identificado' : 'identified'))
                                : '',
                              row.company?.org_name || '',
                              row.client_ip || '',
                              row.country || '',
                              row.device || '',
                              row.os || '',
                              row.browser || '',
                              row.channel || '',
                              row.utm_campaign || '',
                            ]
                              .filter(Boolean)
                              .join(' · ') || (spanish ? 'sin metadatos' : 'no metadata')}
                          </span>
                        </span>
                        <span className="razz-session-meta">
                          <span>{row.pageviews} pv</span>
                          <span title={spanish
                            ? `Duración ${formatDuration(row.duration_s)} · última actividad ${row.last_at || '—'}`
                            : `Duration ${formatDuration(row.duration_s)} · last activity ${row.last_at || '—'}`}
                          >
                            {formatTimeAgo(row.last_at, spanish ? 'es' : 'en', nowMs)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {activeSession ? (
                <article className="razz-session-detail">
                  <header className="razz-session-detail-head">
                    <div>
                      <p className="razz-pref-kicker">
                        {isActiveSession(activeSession.last_at)
                          ? (spanish ? 'En vivo' : 'Live now')
                          : (spanish ? 'Sesión' : 'Session')}
                      </p>
                      <h3>{activeSession.entry_path || '/'}</h3>
                      <p className="razz-muted">
                        {formatChartDay(
                          String(activeSession.started_at || '').slice(0, 10),
                          spanish ? 'es' : 'en-US',
                          'full',
                        )}
                        {' · '}
                        {formatDuration(activeSession.duration_s)}
                        {' · '}
                        {activeSession.pageviews} {spanish ? 'vistas' : 'pageviews'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="dashboard-btn"
                      disabled={excludeBusy}
                      title={spanish
                        ? 'Excluir este visitor_id / IP (pruebas propias)'
                        : 'Exclude this visitor_id / IP (your own tests)'}
                      onClick={() => void excludeSessionIdentity(activeSession)}
                    >
                      {spanish ? 'Excluir de Traffic' : 'Exclude from Traffic'}
                    </button>
                  </header>
                  <div className="razz-session-chip-grid" aria-label={spanish ? 'Metadatos' : 'Metadata'}>
                    {[
                      [spanish ? 'IP de origen' : 'Source IP', activeSession.client_ip || '—'],
                      [spanish ? 'País' : 'Country', activeSession.country || '—'],
                      [spanish ? 'Región / estado' : 'Region / state', activeSession.region || '—'],
                      [spanish ? 'Ciudad' : 'City', activeSession.city || '—'],
                      [
                        spanish ? 'Autenticidad' : 'Authenticity',
                        activeSession.authenticity
                          ? `${activeSession.authenticity.label || '—'} (${activeSession.authenticity.score ?? '—'})`
                          : '—',
                      ],
                      [spanish ? 'Dispositivo' : 'Device', activeSession.device || '—'],
                      [spanish ? 'Sistema' : 'OS', activeSession.os || '—'],
                      [spanish ? 'Navegador' : 'Browser', activeSession.browser || '—'],
                      [
                        spanish ? 'Bot / crawler' : 'Bot / crawler',
                        activeSession.is_bot
                          ? (activeSession.bot_name || (spanish ? 'Sí' : 'Yes'))
                          : (spanish ? 'No' : 'No'),
                      ],
                      [spanish ? 'Canal' : 'Channel', activeSession.channel || '—'],
                      [
                        spanish ? 'Llegó desde' : 'Came from',
                        activeSession.landing_referrer
                          || activeSession.referrer_url
                          || activeSession.referrer
                          || '—',
                      ],
                      [spanish ? 'Idioma' : 'Language', activeSession.language || '—'],
                      [
                        spanish ? 'Ancho de pantalla' : 'Screen width',
                        activeSession.screen_width != null ? `${activeSession.screen_width}px` : '—',
                      ],
                      [spanish ? 'Entrada' : 'Entry', activeSession.entry_path || '—'],
                      [spanish ? 'Salida' : 'Exit', activeSession.exit_path || '—'],
                      [
                        'UTM',
                        [activeSession.utm_source, activeSession.utm_medium, activeSession.utm_campaign]
                          .filter(Boolean)
                          .join(' / ') || '—',
                      ],
                      [
                        spanish ? 'Empresa (IP)' : 'Company (IP)',
                        activeSession.company?.org_name
                          ? `${activeSession.company.org_name}${activeSession.company.asn ? ` · AS${activeSession.company.asn}` : ''}`
                          : '—',
                      ],
                      [
                        spanish ? 'Identidad' : 'Identity',
                        activeSession.identity?.identified
                          ? [
                            activeSession.identity.full_name,
                            activeSession.identity.email,
                            activeSession.identity.company_name,
                            activeSession.identity.source ? `(${activeSession.identity.source})` : '',
                          ].filter(Boolean).join(' · ')
                          : (spanish ? 'Anónimo (sin formulario)' : 'Anonymous (no form yet)'),
                      ],
                    ].map(([label, value]) => {
                      const botChip = String(label).startsWith('Bot') && Boolean(activeSession.is_bot);
                      return (
                        <div key={String(label)} className={`razz-session-chip${botChip ? ' is-bot' : ''}`}>
                          <span>{label}</span>
                          <strong title={String(value)}>{value}</strong>
                        </div>
                      );
                    })}
                  </div>
                  <dl className="razz-session-facts razz-session-facts--ids">
                    <div>
                      <dt>{spanish ? 'Visitante' : 'Visitor'}</dt>
                      <dd className="razz-mono">{activeSession.visitor_id || '—'}</dd>
                    </div>
                    <div>
                      <dt>{spanish ? 'Sesión' : 'Session id'}</dt>
                      <dd className="razz-mono">{activeSession.session_id}</dd>
                    </div>
                    <div>
                      <dt>{spanish ? 'Inicio' : 'Started'}</dt>
                      <dd>{activeSession.started_at?.replace('T', ' ').slice(0, 19) || '—'}</dd>
                    </div>
                    <div>
                      <dt>{spanish ? 'Última' : 'Last seen'}</dt>
                      <dd>{activeSession.last_at?.replace('T', ' ').slice(0, 19) || '—'}</dd>
                    </div>
                    {activeSession.referrer_url ? (
                      <div className="razz-session-fact-wide">
                        <dt>{spanish ? 'Referencia completa' : 'Full referrer'}</dt>
                        <dd className="razz-mono" title={activeSession.referrer_url}>{activeSession.referrer_url}</dd>
                      </div>
                    ) : null}
                    {activeSession.user_agent ? (
                      <div className="razz-session-fact-wide">
                        <dt>User-Agent</dt>
                        <dd className="razz-mono" title={activeSession.user_agent}>{activeSession.user_agent}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <h4>{spanish ? 'Línea de tiempo de la visita' : 'Visit timeline'}</h4>
                  <ol className="razz-session-paths razz-session-timeline">
                    {activeSession.paths.map((step, index) => {
                      const dwell = Number(step.dwell_s || 0);
                      const signal = String(step.signal || '');
                      const signalLabel = signal === 'confused_exit'
                        ? (spanish ? 'salida confusa' : 'confused exit')
                        : signal === 'held'
                          ? (spanish ? 'leyó ≥20s' : 'held ≥20s')
                          : signal === 'left_conversion'
                            ? (spanish ? 'salió en conversión' : 'left on conversion')
                            : '';
                      const barPct = Math.max(4, Math.min(100, Math.round((dwell / 60) * 100)));
                      const stepInteractions = sessionInteractions.filter(
                        (item) => item.path === step.path
                          && String(item.at || '').slice(0, 16) === String(step.at || '').slice(0, 16),
                      );
                      return (
                        <li key={`${step.path}-${step.at}-${index}`} className={signal ? `is-${signal}` : undefined}>
                          <span className="razz-path-step">{index + 1}</span>
                          <span className="razz-path-copy">
                            <strong>{step.path}</strong>
                            {step.title ? <span className="razz-muted">{step.title}</span> : null}
                            <span className="razz-muted">
                              {String(step.at || '').replace('T', ' ').slice(11, 19)}
                              {dwell > 0 ? ` · ${dwell}s` : ''}
                              {signalLabel ? ` · ${signalLabel}` : ''}
                            </span>
                            <span className="razz-dwell-bar" aria-hidden="true">
                              <span style={{ width: `${barPct}%` }} />
                            </span>
                            {stepInteractions.length ? (
                              <ul className="razz-interaction-list">
                                {stepInteractions.map((item, interactionIndex) => (
                                  <li key={`${item.event_type}-${item.at}-${interactionIndex}`}>
                                    {item.event_type === 'hover'
                                      ? (spanish ? 'Cursor encima' : 'Hovered')
                                      : item.event_type === 'click'
                                        ? (spanish ? 'Clic' : 'Clicked')
                                        : item.event_type}
                                    {item.label || item.target ? `: ${item.label || item.target}` : ''}
                                    {item.duration_ms ? ` · ${Math.round(item.duration_ms / 1000)}s` : ''}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  {sessionInteractions.length && !activeSession.paths.length ? (
                    <p className="razz-muted">{spanish ? 'Sin páginas, solo interacciones.' : 'No pages, interactions only.'}</p>
                  ) : null}
                </article>
              ) : (
                <p className="razz-muted razz-session-hint">
                  {spanish
                    ? 'Elige una sesión a la izquierda para ver metadatos y el recorrido.'
                    : 'Pick a session on the left for metadata and the path trail.'}
                </p>
              )}
            </div>
          )}
        </section>
      )}
      </div>
    </div>
  );
};

export default RazzmatazzTrafficPanel;
